<template>
    <div>

        <a-action-button @click="visible = true" icon="solar:pen-bold-duotone" intent="warning"></a-action-button>

        <a-popup :visible="visible" @cancel="onCancel">
            <div
                class="tw-mx-auto tw-w-[95%] md:tw-max-w-[800px] tw-overflow-hidden tw-h-fit dark:tw-bg-gray-900 tw-bg-white tw-border tw-border-solid dark:tw-border-gray-400 tw-border-gray-300 tw-shadow-md tw-my-5 tw-rounded">
                <div
                    class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-flex dark:tw-text-gray-700 tw-text-gray-500 tw-items-center tw-gap-4 dark:tw-bg-gray-100 tw-bg-gray-50">
                    {{ $t("titles.edit-item") }}
                </div>

                <div class="tw-p-5 tw-bg-white">
                    <div class="tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-0">
                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField left-icon="solar:user-circle-bold-duotone" v-model="user.name" :label="$t('fields.name')" type="text" :error="errors.name &&
                                $t('errors.' + errors.name, {
                                    field: $t('fields.name'),
                                })
                                " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField left-icon="solar:letter-bold-duotone" v-model="user.email" :label="$t('fields.email')" type="text" :error="errors.email &&
                                $t('errors.' + errors.email, {
                                    field: $t('fields.email'),
                                })
                                " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField left-icon="solar:lock-keyhole-bold-duotone" v-model="user.password" :label="$t('fields.password')" :help="$t('common.leave_empty_to_not_change')" type="text" :hide-errors="!errors.password" :error="errors.password &&
                                $t('errors.' + (errors.password?.includes(':') ? errors.password?.split(':')[0] : errors.password), {
                                    field: $t('fields.password'), number: errors.password?.split(':')[1],
                                })
                                " />
                        </div>

                        <div v-if="false" class="tw-col-span-12 md:tw-col-span-12">
                            <FormVueSelect left-icon="solar:shield-user-bold-duotone" v-model="user.role_id" option-value="id" :label="$t('fields.role')" type="select" :options="roles" :error="errors.role_id &&
                                $t('errors.' + errors.role_id, {
                                    field: $t('fields.role'),
                                })
                                " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormMultiSelect left-icon="solar:shield-user-bold-duotone" v-model="user.roles" option-label="name" option-value="id" :label="$t('fields.role')" type="select" :options="roles" :error="errors.roles &&
                                $t('errors.' + errors.roles, {
                                    field: $t('fields.role'),
                                })
                                " />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormFileUpload
                                v-model="user.profile_image"
                                :label="$t('forms.user.profile-image')"
                                :placeholder="$t('forms.user.profile-image-placeholder')"
                                :help="$t('forms.user.profile-image-help')"
                                left-icon="solar:user-circle-bold-duotone"
                                accept="image/*"
                                :error="errors.profile_image"
                            />
                        </div>

                    </div>
                </div>

                <div
                    class="tw-p-4 tw-text-lg tw-border-t tw-justify-end tw-border-solid tw-font-medium tw-flex dark:tw-text-gray-700 tw-text-gray-500 tw-items-center tw-gap-4 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <a-button :disabled="loading" @click="onCancel" class="tw-min-w-[120px]"
                        intent="simple">{{
                            $t("buttons.cancel") }}</a-button>
                    <a-button :disabled="loading" :loading="loading" @click="onUpdate"
                        class="tw-min-w-[120px]">{{
                            $t("buttons.update") }}</a-button>
                </div>
            </div>
        </a-popup>
    </div>
</template>

<script setup>
import { ref, computed, inject, defineProps, watch } from "vue";
import FormField from "@/components/form/FormField";
import FormVueSelect from "@/components/form/FormVueSelect";
import FormFileUpload from "@/components/form/FormFileUpload";
import FormMultiSelect from "@/components/form/FormMultiSelect.vue";
import { update } from "@/api/users";
import useAlert from "@/composables/useAlert";
import { useI18n } from "vue-i18n";
import { useRoleStore } from "@/stores";

const props = defineProps(['item'])
const i18n = useI18n()
const roleStore = useRoleStore();
const roles = computed(() => roleStore.roles.map(role => ({ ...role, id: role.id, name: role.name.charAt(0).toUpperCase() + role.name.slice(1) })));

const visible = ref(false);
const loading = ref(false);

const user = ref(structuredClone(props.item));
const errors = ref({});
const onUpdated = inject('onUpdated')

// Watch for props changes and update user data
watch(() => props.item, (newItem) => {
    if (newItem) {
        user.value = structuredClone(newItem);
    }
}, { deep: true });

const onCancel = () => {
    user.value = structuredClone(props.item);
    errors.value = {}
    visible.value = false;
};

const onUpdate = async () => {
    loading.value = true;
    errors.value = {}
    
    try {
        let dataToSend;
        
        // Check if a file is uploaded
        if (user.value.profile_image instanceof File) {
            // Use FormData for file uploads
            const formData = new FormData();
            formData.append('name', user.value.name);
            formData.append('email', user.value.email);
            if (user.value.password) {
                formData.append('password', user.value.password);
            }
            if (user.value.role_id) {
                formData.append('role_id', user.value.role_id);
            }
            formData.append('profile_image', user.value.profile_image);

            user.value.roles.forEach(role => {
                formData.append('roles[]', role.id);
            });

            dataToSend = formData;
        } else {
            // Use regular JSON for non-file data
            dataToSend = {
                name: user.value.name,
                email: user.value.email,
                ...(user.value.password && { password: user.value.password }),
                ...(user.value.role_id && { role_id: user.value.role_id }),
                roles: user.value.roles.map(role => role.id)
            };
            
            // If there's an existing profile image URL, include it
            if (user.value.profile_image && typeof user.value.profile_image === 'string') {
                dataToSend.profile_image_url = user.value.profile_image;
            }
        }
        
        const response = await update(user.value.id, dataToSend);
        
        if (response.data.code == 'SUCCESS') {
            useAlert(i18n.t('messages.updated-successfully', { field: i18n.t('fields.user') }))
            onUpdated(response.data.user);
            onCancel();
        }
    } catch (error) {
        if (error.response?.status == 422) {
            for (let e in error.response.data.errors) {
                errors.value[e] = error.response.data.errors[e][0];
            }
        }
    } finally {
        loading.value = false;
    }
};
</script>

<style></style>
